// Smart API Flange Finder — API 6A flange master data.
// Sources: API 6A dimension tables (piping-world.com metric tables, 6B 3K/5K,
// 6BX 3K/10K/15K/20K), API 6A flange bolt & ring chart (stud size × length),
// ASME B16.20 BX ring tables. All linear dims stored in mm.
// 6B: rf = raised face K, grv.pitch = groove pitch dia P, grv.depth = E.
// 6BX: rf = raised face K, grv.od = groove OD G, grv.depth = Q(max).

const T23 = Math.tan((23 * Math.PI) / 180);

// BX ring dimensions (mm): od, height h, width a — ASME B16.20 / API 6A.
export const BX_RINGS = {
  "BX-151": { od: 76.4, h: 9.63, a: 9.63 },
  "BX-152": { od: 84.68, h: 10.24, a: 10.24 },
  "BX-153": { od: 100.94, h: 11.38, a: 11.38 },
  "BX-154": { od: 116.84, h: 12.4, a: 12.4 },
  "BX-155": { od: 147.96, h: 14.22, a: 14.22 },
  "BX-156": { od: 237.92, h: 18.62, a: 18.62 },
  "BX-157": { od: 294.46, h: 20.98, a: 20.98 },
  "BX-158": { od: 352.04, h: 23.14, a: 23.14 },
  "BX-159": { od: 426.72, h: 25.7, a: 25.7 },
  "BX-162": { od: 475.49, h: 14.22, a: 14.22 },
  "BX-164": { od: 570.56, h: 30.1, a: 24.59 },
  "BX-166": { od: 640.03, h: 32.03, a: 26.14 },
  "BX-168": { od: 765.25, h: 35.86, a: 16.05 },
  "BX-169": { od: 173.51, h: 15.85, a: 12.93 },
};

// R/RX ring gasket OD × cross-section width, inches (published ring tables).
export const R_RINGS = {
  "R-24": { od: 4.172, w: 0.438 },
  "R-27": { od: 4.656, w: 0.438 },
  "R-31": { od: 5.297, w: 0.438 },
  "R-35": { od: 5.797, w: 0.438 },
  "R-37": { od: 6.297, w: 0.438 },
  "R-39": { od: 6.797, w: 0.438 },
  "R-41": { od: 7.547, w: 0.438 },
  "R-44": { od: 8.047, w: 0.438 },
  "R-45": { od: 8.734, w: 0.438 },
  "R-46": { od: 8.75, w: 0.5 },
  "R-49": { od: 11.047, w: 0.438 },
  "R-50": { od: 11.156, w: 0.625 },
  "R-53": { od: 13.172, w: 0.438 },
  "R-54": { od: 13.281, w: 0.625 },
  "R-57": { od: 15.438, w: 0.438 },
  "R-66": { od: 19.125, w: 0.625 },
  "R-74": { od: 23.75, w: 0.75 },
};

// API flange bolt make-up torque, ft·lb (lubricated A193 B7/B16 final pass).
export const BOLT_TORQUE_FTLB = {
  '3/4"': 200, '7/8"': 310, '1"': 465, '1-1/8"': 685, '1-1/4"': 960,
  '1-3/8"': 1305, '1-1/2"': 1725, '1-5/8"': 2225, '1-3/4"': 2800,
  '1-7/8"': 3480, '2"': 4265, '2-1/4"': null, '2-1/2"': null, '2-3/4"': null, '3"': null,
};

const FR = { // fraction of inch → decimal
  "3/4": 0.75, "7/8": 0.875, "1": 1, "1-1/8": 1.125, "1-1/4": 1.25,
  "1-3/8": 1.375, "1-1/2": 1.5, "1-5/8": 1.625, "1-3/4": 1.75,
  "1-7/8": 1.875, "2": 2, "2-1/4": 2.25, "2-1/2": 2.5, "2-3/4": 2.75, "3": 3,
};

function boltThread(dia) {
  if (dia === "3/4") return '3/4" - 10 UNC';
  if (dia === "7/8") return '7/8" - 9 UNC';
  return `${dia}" - 8 UN`;
}

// Raw rows: [size, sizeIn, psi, type, ring, bore, od, thk, hub, rf, bc, n, hole, boltDia, studLen, g1, g2, g3]
// 6B:  g1 = groove pitch P (mm), g2 = groove width F (mm), g3 = groove depth E (mm)
// 6BX: g1 = groove OD G (mm),   g2 = null (computed),      g3 = groove depth Q max (mm)
const ROWS = [
  // ---- Type 6B — 3000 psi ----
  ['2-1/16"', 2.0625, 3000, "6B", "R-24", 53.2, 215, 46.1, 104.8, 124, 165.1, 8, 26, "7/8", '6"', 95.25, 11.91, 7.9],
  ['2-9/16"', 2.5625, 3000, "6B", "R-27", 65.9, 245, 49.3, 123.8, 137, 190.5, 8, 29, "1", '7"', 107.95, 11.91, 7.9],
  ['3-1/8"', 3.125, 3000, "6B", "R-31", 81.8, 240, 46.1, 127.0, 156, 190.5, 8, 26, "7/8", '6-1/4"', 123.83, 11.91, 7.9],
  ['4-1/16"', 4.0625, 3000, "6B", "R-37", 108.7, 290, 52.4, 158.8, 181, 235.0, 8, 32, "1-1/8", '7-1/2"', 149.23, 11.91, 7.9],
  ['5-1/8"', 5.125, 3000, "6B", "R-41", 131.0, 350, 58.8, 190.5, 216, 279.4, 8, 35, "1-1/4", '8-1/4"', 180.98, 11.91, 7.9],
  ['7-1/16"', 7.0625, 3000, "6B", "R-45", 181.0, 380, 63.5, 235.0, 241, 317.5, 12, 32, "1-1/8", '8-1/2"', 211.15, 11.91, 7.9],
  ['9"', 9, 3000, "6B", "R-49", 229.4, 470, 71.5, 298.5, 308, 393.7, 12, 39, "1-3/8", '9-1/2"', 269.88, 11.91, 7.9],
  ['11"', 11, 3000, "6B", "R-53", 280.2, 545, 77.8, 368.3, 362, 469.9, 16, 39, "1-3/8", '10"', 323.85, 11.91, 7.9],
  ['13-5/8"', 13.625, 3000, "6B", "R-57", 346.9, 610, 87.4, 419.1, 419, 533.4, 20, 39, "1-3/8", '10-3/4"', 381.0, 11.91, 7.9],
  ['16-3/4"', 16.75, 3000, "6B", "R-66", 426.2, 705, 100.1, 508.0, 524, 616.0, 20, 45, "1-5/8", '12-1/4"', 469.9, 16.66, 11.2],
  ['20-3/4"', 20.75, 3000, "6B", "R-74", 527.8, 855, 120.7, 622.9, 648, 749.3, 20, 54, "2", '15"', 584.2, 19.84, 12.7],
  // ---- Type 6B — 5000 psi ----
  ['2-1/16"', 2.0625, 5000, "6B", "R-24", 53.2, 215, 46.1, 104.8, 124, 165.1, 8, 26, "7/8", '6-1/4"', 95.25, 11.91, 7.9],
  ['2-9/16"', 2.5625, 5000, "6B", "R-27", 65.9, 245, 49.3, 123.8, 137, 190.5, 8, 29, "1", '7"', 107.95, 11.91, 7.9],
  ['3-1/8"', 3.125, 5000, "6B", "R-35", 81.8, 265, 55.6, 133.3, 168, 203.2, 8, 32, "1-1/8", '7-3/4"', 136.53, 11.91, 7.9],
  ['4-1/16"', 4.0625, 5000, "6B", "R-39", 108.7, 310, 62.0, 161.9, 194, 241.3, 8, 35, "1-1/4", '8-1/2"', 161.93, 11.91, 7.9],
  ['5-1/8"', 5.125, 5000, "6B", "R-44", 131.0, 375, 81.0, 196.8, 229, 292.1, 8, 42, "1-1/2", '10-3/4"', 193.68, 11.91, 7.9],
  ['7-1/16"', 7.0625, 5000, "6B", "R-46", 181.0, 395, 92.1, 228.6, 248, 317.5, 12, 39, "1-3/8", '11-1/4"', 211.15, 13.49, 9.7],
  ['9"', 9, 5000, "6B", "R-50", 229.4, 485, 103.2, 292.1, 318, 393.7, 12, 45, "1-5/8", '12-1/2"', 269.88, 16.66, 11.2],
  ['11"', 11, 5000, "6B", "R-54", 280.2, 585, 119.1, 368.3, 371, 482.6, 12, 51, "1-7/8", '14-1/2"', 323.85, 16.66, 11.2],
  // ---- Type 6BX — 3000 psi (26-3/4" only) ----
  ['26-3/4"', 26.75, 3000, "6BX", "BX-168", 680.2, 1100, 161.2, 870.0, 832, 1000.1, 24, 54, "2", '17-1/2"', 774.22, null, 21.43],
  // ---- Type 6BX — 10000 psi ----
  ['1-13/16"', 1.8125, 10000, "6BX", "BX-151", 46.8, 185, 42.1, 88.9, 105, 146.1, 8, 23, "3/4", '5-1/4"', 77.77, null, 5.56],
  ['2-1/16"', 2.0625, 10000, "6BX", "BX-152", 53.2, 200, 44.1, 100.0, 111, 158.8, 8, 23, "3/4", '5-1/2"', 86.23, null, 5.95],
  ['2-9/16"', 2.5625, 10000, "6BX", "BX-153", 65.9, 230, 51.2, 120.7, 132, 184.2, 8, 26, "7/8", '6-1/4"', 102.77, null, 6.75],
  ['3-1/16"', 3.0625, 10000, "6BX", "BX-154", 78.6, 270, 58.4, 142.1, 152, 215.9, 8, 29, "1", '7-1/4"', 119.0, null, 7.54],
  ['4-1/16"', 4.0625, 10000, "6BX", "BX-155", 104.0, 315, 70.3, 182.6, 185, 258.8, 8, 32, "1-1/8", '8-1/4"', 150.62, null, 8.33],
  ['5-1/8"', 5.125, 10000, "6BX", "BX-169", 131.0, 360, 79.4, 223.8, 221, 300.0, 12, 32, "1-1/8", '9"', 176.66, null, 9.53],
  ['7-1/16"', 7.0625, 10000, "6BX", "BX-156", 180.2, 480, 103.2, 301.6, 302, 403.2, 12, 42, "1-1/2", '11-3/4"', 241.83, null, 11.11],
  ['9"', 9, 10000, "6BX", "BX-157", 229.4, 550, 123.9, 374.7, 359, 476.3, 16, 42, "1-1/2", '13-1/4"', 299.06, null, 12.7],
  ['11"', 11, 10000, "6BX", "BX-158", 280.2, 655, 141.3, 450.9, 429, 565.2, 16, 48, "1-3/4", '15-1/2"', 357.23, null, 14.29],
  ['13-5/8"', 13.625, 10000, "6BX", "BX-159", 346.9, 770, 168.3, 552.5, 518, 673.1, 20, 51, "1-7/8", '17-3/4"', 432.64, null, 15.88],
  ['16-3/4"', 16.75, 10000, "6BX", "BX-162", 426.2, 870, 168.3, 655.6, 576, 776.3, 24, 51, "1-7/8", '17-3/4"', 478.33, null, 8.33],
  ['18-3/4"', 18.75, 10000, "6BX", "BX-164", 477.0, 1040, 223.1, 752.5, 697, 925.5, 24, 61, "2-1/4", '22-1/2"', 577.9, null, 18.26],
  ['21-1/4"', 21.25, 10000, "6BX", "BX-166", 540.5, 1145, 241.3, 847.7, 781, 1022.4, 24, 67, "2-1/2", '24-1/2"', 647.88, null, 19.05],
  // ---- Type 6BX — 15000 psi ----
  ['1-13/16"', 1.8125, 15000, "6BX", "BX-151", 46.8, 210, 45.3, 97.6, 106, 160.3, 8, 26, "7/8", '5-3/4"', 77.77, null, 5.56],
  ['2-1/16"', 2.0625, 15000, "6BX", "BX-152", 53.2, 220, 50.8, 111.1, 114, 174.6, 8, 26, "7/8", '6-1/4"', 86.23, null, 5.95],
  ['2-9/16"', 2.5625, 15000, "6BX", "BX-153", 65.9, 255, 57.2, 128.6, 133, 200.0, 8, 29, "1", '7"', 102.77, null, 6.75],
  ['3-1/16"', 3.0625, 15000, "6BX", "BX-154", 78.6, 290, 64.3, 154.0, 154, 230.2, 8, 32, "1-1/8", '8"', 119.0, null, 7.54],
  ['4-1/16"', 4.0625, 15000, "6BX", "BX-155", 104.0, 360, 78.6, 195.3, 194, 290.5, 8, 39, "1-3/8", '9-1/2"', 150.62, null, 8.33],
  ['5-1/8"', 5.125, 15000, "6BX", "BX-169", 131.0, 420, 98.5, 244.5, 225, 342.9, 12, 42, "1-1/2", '11-1/2"', 176.66, null, 9.53],
  ['7-1/16"', 7.0625, 15000, "6BX", "BX-156", 180.2, 505, 119.1, 325.4, 305, 428.6, 16, 42, "1-1/2", '13"', 241.83, null, 11.11],
  ['9"', 9, 15000, "6BX", "BX-157", 229.4, 650, 146.1, 431.8, 381, 552.4, 16, 51, "1-7/8", '15-3/4"', 299.06, null, 12.7],
  ['11"', 11, 15000, "6BX", "BX-158", 280.2, 815, 187.4, 584.2, 454, 711.2, 20, 54, "2", '19-1/2"', 357.23, null, 14.29],
  ['13-5/8"', 13.625, 15000, "6BX", "BX-159", 346.9, 885, 204.8, 595.3, 541, 771.5, 20, 61, "2-1/4", '21"', 432.64, null, 15.88],
  ['18-3/4"', 18.75, 15000, "6BX", "BX-164", 477.0, 1160, 255.6, 812.8, 722, 1016.0, 20, 80, "3", '26-3/4"', 577.9, null, 18.26],
  // ---- Type 6BX — 20000 psi ----
  ['1-13/16"', 1.8125, 20000, "6BX", "BX-151", 46.8, 255, 63.5, 133.4, 117, 203.2, 8, 29, "1", '7-3/4"', 77.77, null, 5.56],
  ['2-1/16"', 2.0625, 20000, "6BX", "BX-152", 53.2, 285, 71.5, 154.0, 132, 230.2, 8, 32, "1-1/8", '8-1/2"', 86.23, null, 5.95],
  ['2-9/16"', 2.5625, 20000, "6BX", "BX-153", 65.9, 325, 79.4, 173.0, 151, 261.9, 8, 35, "1-1/4", '9-1/2"', 102.77, null, 6.75],
  ['3-1/16"', 3.0625, 20000, "6BX", "BX-154", 78.6, 355, 85.8, 192.1, 171, 287.3, 8, 39, "1-3/8", '10-1/4"', 119.0, null, 7.54],
  ['4-1/16"', 4.0625, 20000, "6BX", "BX-155", 104.0, 445, 106.4, 242.9, 219, 357.2, 8, 48, "1-3/4", '12-1/4"', 150.62, null, 8.33],
  ['7-1/16"', 7.0625, 20000, "6BX", "BX-156", 180.2, 655, 165.1, 385.8, 352, 554.0, 16, 54, "2", '17-3/4"', 241.83, null, 11.11],
  ['9"', 9, 20000, "6BX", "BX-157", 229.4, 805, 204.8, 481.0, 441, 685.8, 16, 67, "2-1/2", '21-3/4"', 299.06, null, 12.7],
  ['11"', 11, 20000, "6BX", "BX-158", 280.2, 885, 223.9, 566.7, 505, 749.3, 16, 74, "2-3/4", '23-3/4"', 357.23, null, 14.29],
  ['13-5/8"', 13.625, 20000, "6BX", "BX-159", 346.9, 1160, 292.1, 693.7, 614, 1016.0, 20, 80, "3", '30"', 432.64, null, 15.88],
];

function toFrac(dec, denom = 16) {
  const whole = Math.floor(dec + 1e-9);
  let num = Math.round((dec - whole) * denom);
  let d = denom;
  if (num === denom) return `${whole + 1}`;
  while (num > 0 && num % 2 === 0) { num /= 2; d /= 2; }
  if (num === 0) return `${whole}`;
  return whole > 0 ? `${whole}-${num}/${d}` : `${num}/${d}`;
}

export function buildFlange(r) {
  const [size, sizeIn, psi, type, ring, bore, od, thk, hub, rf, bc, n, hole, boltDia, studLen, g1, g2, g3] = r;
  const isBX = type === "6BX";
  const bx = isBX ? BX_RINGS[ring] : null;
  // 6BX groove width at face: ring width + taper opening above ring half-height (23° walls)
  const grvWidth = isBX
    ? (bx ? bx.a + 2 * Math.max(0, g3 - bx.h / 2) * T23 : null)
    : g2;
  const grvOd = isBX ? g1 : g1 + g2;
  const grvId = grvWidth != null ? grvOd - 2 * grvWidth : null;
  const diaDec = FR[boltDia];
  const afIn = 1.5 * diaDec + 0.125; // heavy-hex nut / hammer wrench across flats
  return {
    key: `${size}|${psi}`,
    size, sizeIn, psi, type, ring,
    ringLabel: isBX ? ring : `${ring} / RX-${ring.split("-")[1]}`,
    bar: Math.round(psi / 14.5038),
    boreMm: bore, odMm: od, thkMm: thk, hubMm: hub, rfMm: rf, bcMm: bc,
    nBolts: n, holeMm: hole,
    boltDia, bolt: boltThread(boltDia),
    studLen,
    grvPitchMm: isBX ? null : g1,
    grvOdMm: grvOd, grvIdMm: grvId, grvWidthMm: grvWidth, grvDepthMm: g3,
    gasket: isBX ? (bx ? { odIn: bx.od / 25.4, wIn: bx.a / 25.4 } : null)
      : (R_RINGS[ring] ? { odIn: R_RINGS[ring].od, wIn: R_RINGS[ring].w } : null),
    torque: BOLT_TORQUE_FTLB[`${boltDia}"`] ?? null,
    nutAf: `${toFrac(afIn)}" AF`,
    wrench: `${toFrac(afIn)}" hammer wrench`,
  };
}

export const FLANGES = ROWS.map(buildFlange);

// ---- Search ----------------------------------------------------------------
const norm = (s) => String(s).toLowerCase().replace(/["'\u2033\u201d,]/g, "").replace(/\s+/g, " ").trim();

// Weighted search fields: size > ring > pressure > bolt > dims > stud length.
function fields(f) {
  return [
    { vs: [norm(f.size), norm(f.size).replace(/-/g, " "), f.sizeIn.toFixed(4), f.sizeIn.toFixed(2)], w: 10 },
    { vs: [norm(f.ringLabel), norm(f.ringLabel).replace(/-/g, "")], w: 9 },
    { vs: [String(f.psi), `${f.psi / 1000}k`, `${f.psi} psi`, `${f.bar} bar`], w: 8 },
    { vs: [norm(f.bolt), norm(f.bolt).replace(/-/g, " "), norm(f.boltDia)], w: 5 },
    { vs: [(f.odMm / 25.4).toFixed(2), `${Math.round(f.odMm)}`, (f.boreMm / 25.4).toFixed(2), `${Math.round(f.boreMm)}`], w: 4 },
    { vs: [f.type.toLowerCase()], w: 3 },
    { vs: [norm(f.studLen)], w: 1 },
  ];
}

const INDEX = FLANGES.map((f) => ({ f, flds: fields(f) }));

const expandToken = (t) => {
  const m = t.match(/^(\d+(?:\.\d+)?)k$/);
  if (m) return String(Number(m[1]) * 1000);
  return t;
};

export function searchFlanges(query) {
  const q = norm(query);
  if (!q) return [];
  const tokens = q.split(" ").filter(Boolean).map(expandToken);
  if (!tokens.length) return [];
  const scored = [];
  for (const { f, flds } of INDEX) {
    let score = 0;
    let ok = true;
    for (const t of tokens) {
      let best = 0;
      for (const { vs, w } of flds) {
        for (const v of vs) {
          if (v === t) best = Math.max(best, w * 3);
          else if (v.startsWith(t)) best = Math.max(best, w * 2);
          else if (v.includes(t)) best = Math.max(best, w);
        }
      }
      if (!best) { ok = false; break; }
      score += best;
    }
    if (ok) scored.push({ f, score });
  }
  scored.sort((a, b) => b.score - a.score || a.f.sizeIn - b.f.sizeIn || a.f.psi - b.f.psi);
  return scored.map((s) => s.f);
}

// ---- Unit formatting --------------------------------------------------------
export function fmtDual(mm, metricFirst, dp = 2) {
  if (mm == null) return "—";
  const inch = (mm / 25.4).toFixed(dp);
  const mmv = mm.toFixed(1);
  return metricFirst ? `${mmv} mm (${inch}")` : `${inch}" (${mmv} mm)`;
}

// ---- Favorites (same localStorage key as previous slide rule) ---------------
import { scoped } from "@/lib/accountScope";
const FAVS_KEY = "rsc.flangeFavs.v1";

export function getFlangeFavs() {
  try {
    const raw = JSON.parse(scoped.getItem(FAVS_KEY) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch { return []; }
}

export function toggleFlangeFav(key) {
  const favs = getFlangeFavs();
  const next = favs.includes(key) ? favs.filter((k) => k !== key) : [...favs, key];
  try { scoped.setItem(FAVS_KEY, JSON.stringify(next)); } catch { /* quota */ }
  return next;
}
